// The Meetings surface's client (T59, U22, B306).
//
// MIRROR OF apps/api/src/meeting/store.ts's MeetingRow and MeetingDetail, and of
// MeetingsView in apps/api/src/routes/meetings.ts, field for field. A copy rather than an
// import because the two packages do not depend on one another; the API side's mirror test
// reads this file and fails when a field exists on one side and not the other. Milestone 4
// was bitten by a mock answering a shape the API did not, and the live page went blank.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';
import { dayMonth } from '../day.js';

export interface MeetingRow {
  id: string;
  signalId: string;
  title: string | null;
  startedAt: string;
  endedAt: string | null;
  minutes: number | null;
  isExternal: boolean;
  vendor: string | null;
  /**
   * 'complete', 'pending' or 'failed', from the meeting row. The list carries no count of
   * transcript turns and no speaker list: reading either per row cost 72 seconds on the live
   * store, because the segment policy re-derives signal visibility for every one of 303,905
   * rows. Both are on the meeting page, where the query is one meeting (B319).
   */
  transcriptStatus: string;
  headline: string | null;
  summaryId: string | null;
  classified: boolean;
  /** Why this meeting is on your list: you took part, it was recorded for you, or your group is shared it (R37). */
  via: 'participant' | 'recorded_for' | 'group';
  /** Other recordings of this same meeting on your list: another lane recorded it too, or the recording was restarted. */
  others: Array<{ id: string; vendor: string | null; minutes: number | null; turns: number }>;
  /**
   * Who was on the call with a record, the reader left out, and the companies of the people outside the team with
   * the company the call is filed under, each by name (pipeline F5, B1957; migration 199): the bar's By person and
   * By company. The pipeline reads them onto the reader's card, so the list costs nothing more to read (B319).
   */
  people: Array<{ id: string; name: string }>;
  companies: Array<{ id: string; name: string }>;
  /**
   * Whether this call's page offers you a recording to play (BUGS.md M66, B2506): a copy, a Fathom link, a Teams
   * recording, or your own Notetaker's. False draws the lines tile and 'No recording'. Null (or absent, from an API
   * older than this tab) while the card has not been read for it: the tile keeps the vendor's rule.
   */
  playable?: boolean | null;
}

export interface MeetingBrief {
  purpose: string;
  takeaways: string[];
  topics: Array<{ title: string; points: string[] }>;
}
export interface MeetingActionItem { what: string; who: string | null; at_ms: number | null }

export interface MeetingDecision { what: string; by: string | null }
export interface MeetingCommitment { what: string; who: string | null; when: string | null }

export interface MeetingSummaryView {
  id: string;
  headline: string;
  summary: string;
  decisions: MeetingDecision[];
  commitments: MeetingCommitment[];
  openQuestions: string[];
  brainVersion: string;
  passages: number;
  transcriptChars: number;
  writtenAt: string;
  brief: MeetingBrief | null;
  actionItems: MeetingActionItem[];
  briefBrainVersion: string | null;
  /**
   * U196, module 10 B1720: where each key takeaway was discussed, milliseconds into the call, one per
   * takeaway in order, null where no passage placed it. Found by the server in the turns this reader
   * may see. Optional here only so a page open across a deploy reads an older answer as "no minutes".
   */
  takeawayAt?: Array<number | null>;
  /**
   * Module 10 U199 (module 03 T107): where every other summary point was discussed, each list by
   * position as the page draws it; null where none of that point's words is said. The minutes the
   * call page and the summary mail carry. Optional for the same reason as takeawayAt.
   */
  pointAt?: { topics: Array<Array<number | null>>; decided: Array<number | null>; owed: Array<number | null>; open: Array<number | null> };
}

export type MeetingHowItWent =
  | { state: 'written'; paragraphs: Array<{ text: string; moments: Array<{ id: string; atMs: number }> }> }
  | { state: 'writing' }
  | { state: 'failed' }
  | { state: 'no_transcript' };

export interface MeetingDetail extends MeetingRow {
  /** REQ-2026-10-13-15: the stored read of this external call; null draws nothing. */
  howItWent: MeetingHowItWent | null;
  speakers: string[];
  segments: number;
  summary: MeetingSummaryView | null;
  attendees: Array<{ name: string; jobTitle: string | null; role: string }>;
  siblings: Array<{ id: string; vendor: string | null; startedAt: string; turns: number }>;
  related: { id: string; title: string | null; startedAt: string } | null;
  /** hidden: no recording panel is drawn (REQ-2026-10-13-14: a Notetaker call recorded for someone else, to Exec). */
  recording: { available: boolean; source: 'teams' | 'recall' | 'fathom' | null; note: string | null; hidden?: true };
  turns: Array<{ speaker: string | null; at: number | null; text: string }>;
  /**
   * The tasks from this meeting: the promises drawn from it that are a task, and the tasks a person added from its action
   * items (BUGS.md M28). `done` marks a done one, which stays listed; absent from a server older than it.
   */
  tasks: Array<{ id: string; statement: string; dueAt: string | null; ownerName: string | null; done?: boolean }>;
  /** How many tasks the call made in all; `tasks` stops at 50 (BUGS.md M24, X25). Absent from a server older than it. */
  tasksTotal?: number;
  /**
   * BUGS.md M28: for each action item, in order, the reader's own task from it, or null. 'suggested' is one Cobrain filed
   * for them that Add to tasks accepts; 'open' and 'done' are in their tasks already. Absent from a server older than it.
   */
  actionTasks?: Array<MeetingItemTask | null>;
  /**
   * Module 10 U381: each speaker's share of the speech, longest first and "Unidentified speaker" last, the percentages
   * adding to 100, from the transcript's timings alone; null with no timed segment. Optional so a page open across a
   * deploy reads an older answer as no card.
   */
  airtime?: Array<{ speaker: string; ms: number; pct: number }> | null;
}

/** The reader's task from one action item (BUGS.md M28). */
export interface MeetingItemTask { taskId: string; state: 'suggested' | 'open' | 'done'; dueAt: string; priority: number }

export interface MeetingsView {
  store: { tenantId: string; kind: string; name: string };
  meetings: MeetingRow[];
  emptyBecause: 'none_recorded' | null;
  source: 'cards' | 'live';
  builtAt: string | null;
  /** BUGS.md M18: the list stopped at its ceiling and older calls exist; absent from an older API. */
  more?: boolean;
  /** Whether this person has the My Team tab (B444); no control is shown when false (R5). */
  teamTab: boolean;
  /** Whether this person has the Company tab (T99, B1649); no control is shown when false (R5). */
  companyTab: boolean;
}

/** A row of the My Team tab (T73, B444): a customer call a team-mate was on. */
export interface TeamMeetingRow extends MeetingRow {
  /** The team-mates who were on it, by sign-in name, in order. */
  mates: string[];
  /** The company the call is attached to in the register. */
  company: string | null;
}

export interface TeamMeetingsView {
  store: { tenantId: string; kind: string; name: string };
  meetings: TeamMeetingRow[];
  emptyBecause: 'none_recorded' | null;
  /** BUGS.md M30: the list stopped at its ceiling and older calls exist. Absent from a server older than it. */
  more?: boolean;
}

export type RecordingView =
  | { available: true; source: 'teams'; kind: 'embed'; link: { url: string; mintedAt: string; file: { name: string | null; sizeBytes: number | null; durationMs: number | null; width: number | null; height: number | null } }; expiresInSeconds: number }
  | { available: true; source: 'recall'; kind: 'video'; link: { url: string; mintedAt: string; durationMs: number | null }; expiresInSeconds: number }
  /** T77: Fathom's share link, opened in Fathom; it does not expire. */
  | { available: true; source: 'fathom'; kind: 'link'; link: { url: string }; expiresInSeconds: null }
  /**
   * The copy Cobrain keeps (2026-09-20, the media lane). `url` is always
   * `/api/meetings/{id}/recording/file`, a route on our own API behind the gate the recording
   * route already uses, so nothing is pre-signed and there is no expiry to count down. `source`
   * still says which vendor recorded it, so the page can go on saying where a call came from.
   */
  | { available: true; source: 'teams' | 'recall' | 'fathom'; kind: 'stored'; link: { url: string; bytes: number }; expiresInSeconds: null }
  | { available: false; reason: string };

/**
 * Make a task from an action item (Mike, 2026-09-20: "action items need to have the option to add
 * them to tasks"). It posts the Tasks route's own contract rather than importing the Tasks page's
 * client, so that page and this one can change independently; the API is the contract between them.
 * The route forces a title, a due date and a priority (module 08 rule 9), so the page asks for the
 * two the meeting does not know, and the owner is whoever is signed in.
 */
export const addTask = (body: {
  store: string; title: string; dueAt: string; priority: number; notes: string;
  /** BUGS.md M28: the meeting, the summary the page shows and the item's place in its action items. */
  meetingId?: string; summaryId?: string; item?: number;
}): Promise<{ id: string; how?: 'made' | 'accepted' | 'in tasks already' }> =>
  fetch('/api/tasks', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    .then((r) => json<{ id: string; how?: 'made' | 'accepted' | 'in tasks already' }>(r));

/** The still of a Teams recording, for the row's hero and the player's poster; 404 (hidden) when there is none. */
export const thumbnailUrl = (id: string): string => `/api/meetings/${encodeURIComponent(id)}/thumbnail`;

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export const meetings = (): Promise<MeetingsView> => fetch('/api/meetings', { credentials: 'same-origin' }).then((r) => json<MeetingsView>(r));
/** The My Team tab's rows (B444); a 404 for anyone the server has not switched it on for. */
export const teamMeetings = (): Promise<TeamMeetingsView> => fetch('/api/meetings/team', { credentials: 'same-origin' }).then((r) => json<TeamMeetingsView>(r));
/** The Company tab's rows (T99, B1649): Mine and My Team in one list; a 404 for anyone the server has not switched it on for. */
export const companyMeetings = (): Promise<TeamMeetingsView> => fetch('/api/meetings/company', { credentials: 'same-origin' }).then((r) => json<TeamMeetingsView>(r));
export const meeting = (id: string): Promise<MeetingDetail> => fetch(`/api/meetings/${encodeURIComponent(id)}`, { credentials: 'same-origin' }).then((r) => json<MeetingDetail>(r));
/** Ask for a failed read again. Loading a meeting never calls this. */
export const askHowItWentAgain = (id: string): Promise<{ state: 'writing' }> =>
  fetch(`/api/meetings/${encodeURIComponent(id)}/how-it-went/again`, { method: 'POST', credentials: 'same-origin' })
    .then((r) => json<{ state: 'writing' }>(r));
/**
 * BUGS.md M45 (Mike, 2026-10-07: "I just have the ability to make it visible to conor or not"): one switch per other
 * Exec member who was not on the call, offered only to an Exec member who was. The database decides who is offered one
 * and refuses, in words, a press it does not allow; an empty list draws nothing.
 */
export interface VisibleSwitch { to: string; name: string; on: boolean; changedAt: string | null }
export const visibleTo = (id: string): Promise<{ switches: VisibleSwitch[] }> =>
  fetch(`/api/meetings/${encodeURIComponent(id)}/visible-to`, { credentials: 'same-origin' }).then((r) => json<{ switches: VisibleSwitch[] }>(r));
export const setVisibleTo = (id: string, to: string, on: boolean): Promise<{ switches: VisibleSwitch[] }> =>
  fetch(`/api/meetings/${encodeURIComponent(id)}/visible-to`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ to, on }),
  }).then((r) => json<{ switches: VisibleSwitch[] }>(r));
/**
 * BUGS.md M46 (Mike, 2026-10-07: "all users need the ability to revoke calls"): "Keep to the people on it", offered to any
 * colleague who was on the call; only the one who pressed it undoes it. `state` is null for anyone who may not press.
 */
export interface KeepState { kept: boolean; keptByMe: boolean; keptByName: string | null; keptAt: string | null; held: boolean; heldWaiting: boolean; askedMe: boolean }
export const keepState = (id: string): Promise<{ state: KeepState | null }> =>
  fetch(`/api/meetings/${encodeURIComponent(id)}/keep`, { credentials: 'same-origin' }).then((r) => json<{ state: KeepState | null }>(r));
export const setKeep = (id: string, on: boolean): Promise<{ state: KeepState | null }> =>
  fetch(`/api/meetings/${encodeURIComponent(id)}/keep`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ on }),
  }).then((r) => json<{ state: KeepState | null }>(r));
/** "Conor Rafferty" is "Conor": the switch reads "Visible to Conor". */
export function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** A five-minute link to play the recording, minted now (T63). Ask again when it has run out; never keep it. */
export const recordingLink = (id: string): Promise<RecordingView> => fetch(`/api/meetings/${encodeURIComponent(id)}/recording`, { credentials: 'same-origin' }).then((r) => json<RecordingView>(r));

/**
 * Fathom's share link opened at a second of the call (U196). Verified 2026-09-23 on a live share link:
 * `?timestamp=95` opens the video at 95 seconds, and without it at 0:00 (module 03 DISCOVERY).
 */
export function fathomAt(url: string, ms: number): string {
  try {
    const u = new URL(url);
    u.searchParams.set('timestamp', String(Math.floor(ms / 1000)));
    return u.toString();
  } catch {
    return url;
  }
}

/** Where a recording came from, in the words a person would use rather than the vendor's id. */
export function captureWords(vendor: string | null): string {
  if (vendor === 'recall') return 'Cobrain Notetaker';
  if (vendor === 'teams_graph') return 'Teams';
  if (vendor === 'fathom') return 'Fathom';
  if (vendor === 'motion') return 'Motion';
  return 'recorded';
}

/** Whether a transcript was stored at all, in the words the row shows. */
export function hasTranscript(status: string): boolean {
  return status === 'complete';
}

/** "1h 4m", "22m", or nothing when the meeting has no end recorded. */
export function lengthWords(minutes: number | null): string | null {
  if (minutes === null || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** The date as a person reads it, with today and yesterday named. */
export function whenWords(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (days === 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  if (days < 7) return `${d.toLocaleDateString('en-GB', { weekday: 'long' })} ${time}`;
  return `${dayMonth(d)} ${time}`;
}

/** mm:ss from the transcript's milliseconds, for the turn stamps. */
export function stampWords(ms: number | null): string {
  if (ms === null || ms < 0) return '';
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** "9:38" or "1:02:05" from milliseconds into the recording, as Fathom stamps an action item. */
export function clockWords(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

/** Why a meeting is on your list, in words, for the row and the page. */
export function viaWords(via: MeetingRow['via']): string | null {
  if (via === 'group') return 'Shared with your group';
  if (via === 'recorded_for') return 'Recorded for you';
  return null;
}

/** "With Will Fyler and Vincenzo Zeppillo", "With A, B, C + 2": the team-mates on a call, at most three named. */
export function mateWords(mates: string[]): string | null {
  if (mates.length === 0) return null;
  const named = mates.slice(0, 3);
  const rest = mates.length - named.length;
  const list = named.length === 1 ? named[0] : named.length === 2 ? `${named[0]} and ${named[1]}` : named.join(', ');
  return rest > 0 ? `With ${list} + ${rest}` : `With ${list}`;
}

/** The team-mates on the list, most calls first, then by name: the filter's choices. */
export function matesByCalls(rows: Array<{ mates: string[] }>): Array<{ name: string; calls: number }> {
  const n = new Map<string, number>();
  for (const r of rows) for (const m of r.mates) n.set(m, (n.get(m) ?? 0) + 1);
  return [...n.entries()].map(([name, calls]) => ({ name, calls })).sort((a, b) => b.calls - a.calls || a.name.localeCompare(b.name));
}
